import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { isSuperAdmin, tierLabel } from '@foundry/shared';
import { useMe } from '../../api/auth.js';
import {
  useSetTierCallOptions,
  useTierConfig,
  useUpdateCallOption,
  useUpdateTierConfig,
} from '../../api/config.js';
import type { CallOption, TierCallOption, TierConfigRow } from '../../api/types.js';
import { Alert, Button, Card, Field, Spinner } from '../../components/ui.js';

/** Super-admin configuration of tiers (hours, per-call caps) and booking links. */
export function TiersPage() {
  const { data: user } = useMe();
  const cfg = useTierConfig();

  if (user && !isSuperAdmin(user.role)) return <Navigate to="/console" replace />;
  if (cfg.isPending) return <Spinner />;
  if (cfg.isError || !cfg.data) return <Alert>Could not load configuration.</Alert>;

  const { tierConfig, callOptions, tierCallOptions } = cfg.data;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-title text-3xl tracking-wide">Tiers &amp; calls</h1>
        <p className="opacity-70">
          Configure each package&apos;s development-hour allowance, which call types it shows, and
          how many of each it may book per month. The booking link is set per call type below.
        </p>
      </div>

      <Card>
        <h2 className="mb-1 font-title text-2xl tracking-wide">Call links</h2>
        <p className="mb-4 text-sm opacity-70">The booking URL used for each call type.</p>
        <div className="flex flex-col gap-3">
          {callOptions.map((o) => (
            <CallOptionRow key={o.key} option={o} />
          ))}
        </div>
      </Card>

      <Card>
        <h2 className="mb-1 font-title text-2xl tracking-wide">Tiers</h2>
        <p className="mb-4 text-sm opacity-70">
          Monthly development-hour allowance, and the call types each tier shows with their monthly
          caps. A cap of 0 shows the link but routes the founder to upgrade.
        </p>
        <div className="flex flex-col gap-4">
          {tierConfig.map((t) => (
            <TierRow
              key={t.tier}
              config={t}
              callOptions={callOptions}
              tierOptions={tierCallOptions.filter((x) => x.tier === t.tier)}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

function CallOptionRow({ option }: { option: CallOption }) {
  const update = useUpdateCallOption();
  const [label, setLabel] = useState(option.label);
  const [link, setLink] = useState(option.link);
  const dirty = label !== option.label || link !== option.link;

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md bg-neutral/50 p-3">
      <span className="w-24 text-xs uppercase tracking-wide opacity-60">
        {option.durationMinutes}m
      </span>
      <div className="min-w-40 flex-1">
        <Field label="Label" value={label} onChange={(e) => setLabel(e.target.value)} />
      </div>
      <div className="min-w-64 flex-[2]">
        <Field
          label="Booking link"
          placeholder="https://…"
          value={link}
          onChange={(e) => setLink(e.target.value)}
        />
      </div>
      <Button
        variant="secondary"
        disabled={!dirty || update.isPending}
        onClick={() => update.mutate({ key: option.key, label, link })}
      >
        {update.isPending ? 'Saving…' : 'Save'}
      </Button>
    </div>
  );
}

/** The editable per-option state: whether the tier shows it, and its monthly cap. */
interface OptionState {
  enabled: boolean;
  cap: string;
}

function initialOptionState(
  callOptions: CallOption[],
  tierOptions: TierCallOption[],
): Record<string, OptionState> {
  const byKey = new Map(tierOptions.map((o) => [o.optionKey, o.maxPerMonth]));
  const state: Record<string, OptionState> = {};
  for (const o of callOptions) {
    const cap = byKey.get(o.key);
    state[o.key] = { enabled: cap !== undefined, cap: String(cap ?? 0) };
  }
  return state;
}

function TierRow({
  config,
  callOptions,
  tierOptions,
}: {
  config: TierConfigRow;
  callOptions: CallOption[];
  tierOptions: TierCallOption[];
}) {
  const updateTier = useUpdateTierConfig();
  const setOptions = useSetTierCallOptions();
  const [hours, setHours] = useState(String(config.workingHours));
  const [opts, setOpts] = useState<Record<string, OptionState>>(
    initialOptionState(callOptions, tierOptions),
  );

  const initial = initialOptionState(callOptions, tierOptions);
  const dirtyHours = Number(hours) !== config.workingHours;
  const dirtyOptions = callOptions.some((o) => {
    const a = opts[o.key];
    const b = initial[o.key];
    return a?.enabled !== b?.enabled || (a?.enabled && a.cap !== b?.cap);
  });
  const dirty = dirtyHours || dirtyOptions;
  const busy = updateTier.isPending || setOptions.isPending;

  const setOpt = (key: string, patch: Partial<OptionState>): void =>
    setOpts((s) => ({ ...s, [key]: { ...s[key]!, ...patch } }));

  const onSave = (): void => {
    if (dirtyHours) updateTier.mutate({ tier: config.tier, workingHours: Number(hours) });
    if (dirtyOptions) {
      const options = callOptions
        .filter((o) => opts[o.key]?.enabled)
        .map((o) => ({ optionKey: o.key, maxPerMonth: Number(opts[o.key]?.cap ?? 0) }));
      setOptions.mutate({ tier: config.tier, options });
    }
  };

  return (
    <div className="rounded-md bg-neutral/50 p-4">
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <span className="w-20 font-title text-lg tracking-wide">{tierLabel(config.tier)}</span>
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">Dev hours / month</span>
          <input
            type="number"
            min={0}
            className="w-32 rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
        </label>
        <Button disabled={!dirty || busy} onClick={onSave}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </div>
      <div className="flex flex-col gap-2">
        {callOptions.map((o) => {
          const st = opts[o.key];
          return (
            <div key={o.key} className="flex flex-wrap items-center gap-3 text-sm">
              <label className="flex min-w-56 items-center gap-2">
                <input
                  type="checkbox"
                  checked={st?.enabled ?? false}
                  onChange={(e) => setOpt(o.key, { enabled: e.target.checked })}
                />
                {o.label}
              </label>
              <label className="flex items-center gap-2">
                <span className="opacity-70">Max / month</span>
                <input
                  type="number"
                  min={0}
                  disabled={!st?.enabled}
                  className="w-24 rounded-md border border-secondary/20 bg-white px-2 py-1 outline-none focus:border-primary disabled:opacity-40"
                  value={st?.cap ?? '0'}
                  onChange={(e) => setOpt(o.key, { cap: e.target.value })}
                />
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
